import './Followers.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useRoute } from 'react-router5';
import { useDispatch, useSelector } from 'react-redux';

import { FollowersProps } from '$core/Followers/Followers.interface';
import { Header, Placeholder, ScrollView, SearchInput, Tabs, TabsItem } from '$uikit';
import {
  useCurrentUserInfo,
  useGroupInfo,
  useIsFirstRender,
  useTranslator,
  useUserInfo,
} from '$hooks';
import { showModal } from '$navigation/helper';
import {
  followersActions,
  followersForUserSelector,
  followersSelector,
} from '$store/followers';
import { RootState } from '$store/rootReducer';
import { Loader, MetaTags } from '$shared/components';
import { UserCell } from '$shared/components/UserCell/UserCell';
import { formatBigNumber } from '$utils';
import { editGroupSelector } from '$store/editGroup';
import router, { isBackNavigation } from '$navigation/router';
import { mainSelector } from '$store/main';
import { FollowersBottomSheet } from './FollowersBottomSheet/FollowersBottomSheet';

export const Followers: FC<FollowersProps> = ({ userId, isOutbox = false }) => {
  const user = useUserInfo(userId);
  const group = useGroupInfo(-userId);
  const [tab, setTab] = useState(isOutbox ? 'outbox' : 'inbox');
  const dispatch = useDispatch();
  const {
    isLoading,
    inbox,
    outbox,
    inboxNextFrom,
    outboxNextFrom,
    isInboxLoadingMore,
    isOutboxLoadingMore,
  } = useSelector((state: RootState) => followersForUserSelector(state, userId));
  const { admins } = useSelector(editGroupSelector);
  const { route } = useRoute();
  const isPromote = !!route.params?.promote && userId < 0;
  const isBack = isBackNavigation();
  const [query, setQuery] = useState(route.params?.query ?? '');
  const [bottomSheetUserId, setBottomSheetUserId] = useState<null | number>(null);
  const { following } = useSelector(followersSelector);
  const { users } = useSelector(mainSelector);
  const { id: currentUserId } = useCurrentUserInfo();
  const t = useTranslator();
  const isFirstRender = useIsFirstRender();

  useEffect(() => {
    if (isFirstRender && isBack) {
      return;
    }

    const params: any = { ...route.params };
    if (query && query.length > 0) {
      if (params.query === query) {
        return;
      }

      params.query = query;
    } else {
      delete params.query;
    }

    router.navigate(route.name, params, { replace: true, urlParamsEncoding: false });

    let timer = setTimeout(
      () => {
        dispatch(followersActions.load(userId));
      },
      isFirstRender ? 0 : 600,
    );

    return () => {
      clearTimeout(timer);
    };
  }, [query, isBack]);

  const handleChange = useCallback(
    (newTab) => {
      if (newTab === 'inbox') {
        showModal(`followers${userId}`, true);
      } else {
        showModal(`followed${userId}`, true);
      }
      setTab(newTab);
    },
    [setTab],
  );

  const adminsMap = useMemo(() => {
    const adminsMap = {};
    for (let admin of admins) {
      adminsMap[admin.userId] = true;
    }
    return adminsMap;
  }, [admins]);

  const items = useMemo(() => {
    return tab === 'inbox' ? inbox : outbox;
  }, [tab, inbox, outbox]);

  const isLoadingMore = useMemo(() => {
    return tab === 'inbox' ? isInboxLoadingMore : isOutboxLoadingMore;
  }, [tab, isInboxLoadingMore, isOutboxLoadingMore]);

  const nextFrom = useMemo(() => {
    return tab === 'inbox' ? inboxNextFrom : outboxNextFrom;
  }, [tab, inboxNextFrom, outboxNextFrom]);

  const title = useMemo(() => {
    if (userId < 0) {
      return (
        formatBigNumber(group?.followers ?? 0) +
        ' ' +
        t('followers_count_pref', { count: group?.followers ?? 0 })
      );
    } else {
      return `${user?.firstName} ${user?.lastName}`;
    }
  }, [userId, user, group, t]);

  const metaTitle = useMemo(() => {
    if (userId < 0) {
      return `${group?.name} | Followers | Ton.Place`;
    } else {
      return `${user?.firstName} ${user?.lastName} | Followers | Ton.Place`;
    }
  }, [userId, user, group]);

  const metaDescription = useMemo(() => {
    if (userId < 0) {
      return (
        formatBigNumber(group?.followers ?? 0) +
        ' ' +
        t('followers_count_pref', { count: group?.followers ?? 0 })
      );
    } else {
      return (
        formatBigNumber(user?.followers ?? 0) +
        ' ' +
        t('followed_count_pref', { count: user?.followers ?? 0 })
      );
    }
  }, [userId, user, group, t]);

  const handleSearchChange = useCallback((newQuery) => {
    setQuery(newQuery);
  }, []);

  function renderContent() {
    if (!items.length) {
      if (isLoading) {
        return <Loader isWrapped />;
      }

      return (
        <Placeholder>
          {t(tab === 'outbox' ? 'following_empty' : 'followers_empty')}
        </Placeholder>
      );
    }

    return items.map((item) => (
      <UserCell
        key={`${item.type}_${item.objectId}`}
        objectId={item.objectId}
        isFollowed={false}
        isGroup={item.type === 'group'}
        isPromote={isPromote}
        targetId={userId}
        skipButton={isPromote && adminsMap[item.objectId]}
        isMoreBtnShown={
          item.type === 'user' && tab === 'inbox' && userId === currentUserId
        }
        // onMoreBtnClick={setBottomSheetUserId}
        isMessageBtnShown={userId !== currentUserId || tab !== 'inbox'}
      />
    ));
  }

  return (
    <>
      <MetaTags title={metaTitle} description={metaDescription} />
      <Header showBackButton>{title}</Header>
      <ScrollView
        onLoadMore={() => dispatch(followersActions.loadMore({ userId, tab }))}
        canLoadMore={!isLoading && !isLoadingMore && !!nextFrom}
        isLoadingMore={isLoadingMore}
      >
        <SearchInput
          query={query}
          onChange={handleSearchChange}
          placeholder={t('search')}
        />
        {(!isLoading || items.length > 0) && userId > 0 && (
          <div className="Followers__tabs_wrap">
            <Tabs selected={tab} onChange={handleChange}>
              <TabsItem value="inbox" counter={user?.followers ?? 0}>
                {t('followers_tab_inbox')}
              </TabsItem>
              <TabsItem value="outbox" counter={user?.followed ?? 0}>
                {t('followers_tab_outbox')}
              </TabsItem>
            </Tabs>
          </div>
        )}
        {renderContent()}
        {!!bottomSheetUserId && (
          <FollowersBottomSheet
            buttons="delete"
            user={users[bottomSheetUserId]}
            onClose={() => setBottomSheetUserId(null)}
          />
        )}
      </ScrollView>
    </>
  );
};
